import { useState, FormEvent, ChangeEvent } from 'react';
import * as authService from '../services/auth';
import { setTokens } from '../services/authService';
import styles from './AuthPage.module.css';

/* ---------- Types ---------- */

type AuthMode = 'login' | 'register' | 'forgot-password';

type RegisterStep =
  | 'credentials'
  | 'nickname'
  | 'privacy-consent'
  | 'email-verify'
  | 'done';

type LoginStep = 'credentials' | 'done';

type ResetStep = 'enter-email' | 'enter-code' | 'done';

interface FieldErrors {
  email?: string;
  password?: string;
  code?: string;
  nickname?: string;
  consent?: string;
  newPassword?: string;
}

const REGISTER_STEPS: RegisterStep[] = [
  'credentials',
  'nickname',
  'privacy-consent',
  'email-verify',
];

/* ---------- Component ---------- */

export function AuthPage({ onLoginSuccess }: { onLoginSuccess?: () => void } = {}) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Shared form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  // Registration state
  const [registerStep, setRegisterStep] = useState<RegisterStep>('credentials');
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [nickname, setNickname] = useState('');

  // Login state
  const [loginStep, setLoginStep] = useState<LoginStep>('credentials');

  // Password reset state
  const [resetStep, setResetStep] = useState<ResetStep>('enter-email');
  const [newPassword, setNewPassword] = useState('');

  /* ---------- Helpers ---------- */

  function clearErrors() {
    setGeneralError('');
    setFieldErrors({});
  }

  function switchMode(newMode: AuthMode) {
    clearErrors();
    setMode(newMode);
    setEmail('');
    setPassword('');
    setCode('');
    setRegisterStep('credentials');
    setLoginStep('credentials');
    setResetStep('enter-email');
    setPrivacyConsent(false);
    setNickname('');
    setNewPassword('');
  }

  function validateEmail(value: string): string | undefined {
    if (!value.trim()) return 'Email is required';
    // Basic RFC-5322-ish check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address';
    return undefined;
  }

  function validatePassword(value: string): string | undefined {
    if (!value) return 'Password is required';
    if (value.length < 8) return 'Password must be at least 8 characters';
    return undefined;
  }

  function validateCode(value: string): string | undefined {
    if (!value.trim()) return 'Code is required';
    if (!/^\d{6}$/.test(value.trim())) return 'Enter a 6-digit code';
    return undefined;
  }

  function validateNickname(value: string): string | undefined {
    if (!value) return 'Nickname is required';
    if (value.length < 5) return 'Nickname must be at least 5 characters';
    if (value.length > 15) return 'Nickname must be at most 15 characters';
    if (/\s/.test(value)) return 'Nickname cannot contain spaces';
    if (!/^[a-zA-Z0-9_-]+$/.test(value))
      return 'Only letters, numbers, hyphens, and underscores allowed';
    return undefined;
  }

  /* ---------- Registration handlers ---------- */

  async function handleRegisterCredentials(e: FormEvent) {
    e.preventDefault();
    clearErrors();

    const errors: FieldErrors = {};
    errors.email = validateEmail(email);
    errors.password = validatePassword(password);
    if (errors.email || errors.password) {
      setFieldErrors(errors);
      return;
    }

    // Move to nickname step (nickname collected before API call)
    setRegisterStep('nickname');
  }

  function handleNicknameStep(e: FormEvent) {
    e.preventDefault();
    clearErrors();

    const nickErr = validateNickname(nickname);
    if (nickErr) {
      setFieldErrors({ nickname: nickErr });
      return;
    }

    // Move to privacy consent step
    setRegisterStep('privacy-consent');
  }

  async function handlePrivacyConsent(e: FormEvent) {
    e.preventDefault();
    clearErrors();

    if (!privacyConsent) {
      setFieldErrors({ consent: 'You must consent to the privacy policy to continue' });
      return;
    }

    setLoading(true);
    try {
      await authService.register(email, password, nickname);
      setRegisterStep('email-verify');
      setCode('');
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailVerify(e: FormEvent) {
    e.preventDefault();
    clearErrors();

    const codeErr = validateCode(code);
    if (codeErr) {
      setFieldErrors({ code: codeErr });
      return;
    }

    setLoading(true);
    try {
      await authService.confirmEmail(email, code.trim());
      setRegisterStep('done');
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Email verification failed');
    } finally {
      setLoading(false);
    }
  }

  /* ---------- Login handlers ---------- */

  async function handleLoginCredentials(e: FormEvent) {
    e.preventDefault();
    clearErrors();

    const errors: FieldErrors = {};
    errors.email = validateEmail(email);
    errors.password = validatePassword(password);
    if (errors.email || errors.password) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const res = await authService.login(email, password);
      // Store tokens so useAuth detects the authenticated state and redirects to lobby
      setTokens({
        idToken: res.id_token,
        accessToken: res.access_token,
        expiresAt: Date.now() + res.expires_in * 1000,
      });
      setLoginStep('done');
      onLoginSuccess?.();
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }

  /* ---------- Password reset handlers ---------- */

  async function handleForgotEmail(e: FormEvent) {
    e.preventDefault();
    clearErrors();

    const emailErr = validateEmail(email);
    if (emailErr) {
      setFieldErrors({ email: emailErr });
      return;
    }

    setLoading(true);
    try {
      await authService.forgotPassword(email);
      setResetStep('enter-code');
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();
    clearErrors();

    const errors: FieldErrors = {};
    errors.code = validateCode(code);
    errors.newPassword = validatePassword(newPassword);
    if (errors.code || errors.newPassword) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(email, code.trim(), newPassword);
      setResetStep('done');
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setLoading(false);
    }
  }

  /* ---------- Render helpers ---------- */

  function renderStepIndicator() {
    if (mode !== 'register') return null;
    const currentIdx = REGISTER_STEPS.indexOf(registerStep);
    return (
      <div className={styles.stepIndicator} aria-label="Registration progress">
        {REGISTER_STEPS.map((step, idx) => {
          let className = styles.stepDot;
          if (idx < currentIdx) className += ` ${styles.stepDotComplete}`;
          else if (idx === currentIdx) className += ` ${styles.stepDotActive}`;
          return <span key={step} className={className} aria-hidden="true" />;
        })}
      </div>
    );
  }

  function renderGeneralError() {
    if (!generalError) return null;
    return (
      <div className={styles.generalError} role="alert">
        {generalError}
      </div>
    );
  }

  /* ---------- Registration views ---------- */

  function renderRegisterCredentials() {
    return (
      <form onSubmit={handleRegisterCredentials} noValidate>
        <h1 className={styles.title}>Create Account</h1>
        <p className={styles.subtitle}>Join the mushroom forest</p>
        {renderStepIndicator()}
        {renderGeneralError()}

        <div className={styles.formGroup}>
          <label htmlFor="reg-email" className={styles.label}>Email</label>
          <input
            id="reg-email"
            type="email"
            className={`${styles.input} ${fieldErrors.email ? styles.inputError : ''}`}
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
          />
          {fieldErrors.email && <p className={styles.errorText}>{fieldErrors.email}</p>}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="reg-password" className={styles.label}>Password</label>
          <input
            id="reg-password"
            type="password"
            className={`${styles.input} ${fieldErrors.password ? styles.inputError : ''}`}
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            placeholder="Minimum 8 characters"
            autoComplete="new-password"
          />
          {fieldErrors.password && <p className={styles.errorText}>{fieldErrors.password}</p>}
        </div>

        <button type="submit" className={styles.button} disabled={loading}>
          Continue
        </button>

        <div className={styles.footer}>
          <button type="button" className={styles.linkButton} onClick={() => switchMode('login')}>
            Already have an account? Sign in
          </button>
        </div>
      </form>
    );
  }

  function renderPrivacyConsent() {
    return (
      <form onSubmit={handlePrivacyConsent} noValidate>
        <h1 className={styles.title}>Privacy Consent</h1>
        <p className={styles.subtitle}>Please review and accept before continuing</p>
        {renderStepIndicator()}
        {renderGeneralError()}

        <div className={styles.consentGroup}>
          <input
            id="privacy-consent"
            type="checkbox"
            className={styles.checkbox}
            checked={privacyConsent}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPrivacyConsent(e.target.checked)}
          />
          <label htmlFor="privacy-consent" className={styles.consentLabel}>
            I have read and agree to the{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            . I consent to the processing of my personal data as described.
          </label>
        </div>
        {fieldErrors.consent && <p className={styles.errorText}>{fieldErrors.consent}</p>}

        <button type="submit" className={styles.button} disabled={loading || !privacyConsent}>
          {loading ? 'Creating account...' : 'Create Account'}
        </button>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setRegisterStep('credentials')}
          >
            Back
          </button>
        </div>
      </form>
    );
  }

  function renderEmailVerify() {
    return (
      <form onSubmit={handleEmailVerify} noValidate>
        <h1 className={styles.title}>Check Your Email</h1>
        <p className={styles.subtitle}>
          We sent a 6-digit code to <strong>{email}</strong>. Enter it below to continue.
        </p>
        {renderStepIndicator()}
        {renderGeneralError()}

        <div className={styles.formGroup}>
          <label htmlFor="email-verify-code" className={styles.label}>Confirmation Code</label>
          <input
            id="email-verify-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            className={`${styles.input} ${styles.codeInput} ${fieldErrors.code ? styles.inputError : ''}`}
            value={code}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
            placeholder="000000"
            autoComplete="one-time-code"
            autoFocus
          />
          {fieldErrors.code && <p className={styles.errorText}>{fieldErrors.code}</p>}
        </div>

        <button type="submit" className={styles.button} disabled={loading}>
          {loading ? 'Verifying…' : 'Verify Email'}
        </button>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setRegisterStep('privacy-consent')}
          >
            Back
          </button>
        </div>
      </form>
    );
  }

  function renderNicknameStep() {
    return (
      <form onSubmit={handleNicknameStep} noValidate>
        <h1 className={styles.title}>Choose Your Nickname</h1>
        <p className={styles.subtitle}>This is how other players will see you</p>
        {renderStepIndicator()}
        {renderGeneralError()}

        <div className={styles.formGroup}>
          <label htmlFor="nickname" className={styles.label}>Nickname</label>
          <input
            id="nickname"
            type="text"
            className={`${styles.input} ${fieldErrors.nickname ? styles.inputError : ''}`}
            value={nickname}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setNickname(e.target.value)}
            placeholder="ForestWanderer"
            maxLength={15}
            autoComplete="username"
            autoFocus
          />
          {fieldErrors.nickname && <p className={styles.errorText}>{fieldErrors.nickname}</p>}
          <p className={styles.nicknameHint}>
            5–15 characters. Letters, numbers, hyphens, and underscores only.
          </p>
        </div>

        <button type="submit" className={styles.button} disabled={loading}>
          Continue
        </button>

        <div className={styles.footer}>
          <button type="button" className={styles.linkButton} onClick={() => setRegisterStep('credentials')}>
            Back
          </button>
        </div>
      </form>
    );
  }

  function renderRegisterDone() {
    return (
      <div>
        <h1 className={styles.title}>Welcome to the Forest! 🍄</h1>
        <p className={styles.subtitle}>Your account is ready. Sign in to pick your avatar and start playing.</p>
        <button
          type="button"
          className={styles.button}
          onClick={() => switchMode('login')}
        >
          Sign In
        </button>
      </div>
    );
  }

  /* ---------- Login views ---------- */

  function renderLoginCredentials() {
    return (
      <form onSubmit={handleLoginCredentials} noValidate>
        <h1 className={styles.title}>Sign In</h1>
        <p className={styles.subtitle}>Welcome back to the mushroom forest</p>
        {renderGeneralError()}

        <div className={styles.formGroup}>
          <label htmlFor="login-email" className={styles.label}>Email</label>
          <input
            id="login-email"
            type="email"
            className={`${styles.input} ${fieldErrors.email ? styles.inputError : ''}`}
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
          />
          {fieldErrors.email && <p className={styles.errorText}>{fieldErrors.email}</p>}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="login-password" className={styles.label}>Password</label>
          <input
            id="login-password"
            type="password"
            className={`${styles.input} ${fieldErrors.password ? styles.inputError : ''}`}
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            placeholder="Enter your password"
            autoComplete="current-password"
          />
          {fieldErrors.password && <p className={styles.errorText}>{fieldErrors.password}</p>}
        </div>

        <button type="submit" className={styles.button} disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>

        <div className={styles.footer}>
          <button type="button" className={styles.linkButton} onClick={() => switchMode('forgot-password')}>
            Forgot password?
          </button>
          <button type="button" className={styles.linkButton} onClick={() => switchMode('register')}>
            Create account
          </button>
        </div>
      </form>
    );
  }

  function renderLoginDone() {
    // In a real app, this would redirect to the lobby
    return (
      <div>
        <h1 className={styles.title}>Signed In! 🍄</h1>
        <p className={styles.subtitle}>Redirecting to the lobby...</p>
      </div>
    );
  }

  /* ---------- Password reset views ---------- */

  function renderResetEmail() {
    return (
      <form onSubmit={handleForgotEmail} noValidate>
        <h1 className={styles.title}>Reset Password</h1>
        <p className={styles.subtitle}>Enter your email to receive a reset code</p>
        {renderGeneralError()}

        <div className={styles.formGroup}>
          <label htmlFor="reset-email" className={styles.label}>Email</label>
          <input
            id="reset-email"
            type="email"
            className={`${styles.input} ${fieldErrors.email ? styles.inputError : ''}`}
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
          />
          {fieldErrors.email && <p className={styles.errorText}>{fieldErrors.email}</p>}
        </div>

        <button type="submit" className={styles.button} disabled={loading}>
          {loading ? 'Sending...' : 'Send Reset Code'}
        </button>

        <div className={styles.footer}>
          <button type="button" className={styles.linkButton} onClick={() => switchMode('login')}>
            Back to sign in
          </button>
        </div>
      </form>
    );
  }

  function renderResetCode() {
    return (
      <form onSubmit={handleResetPassword} noValidate>
        <h1 className={styles.title}>Enter Reset Code</h1>
        <p className={styles.subtitle}>Check your email for the 6-digit code</p>
        {renderGeneralError()}

        <div className={styles.formGroup}>
          <label htmlFor="reset-code" className={styles.label}>Reset Code</label>
          <input
            id="reset-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            className={`${styles.input} ${styles.codeInput} ${fieldErrors.code ? styles.inputError : ''}`}
            value={code}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
            placeholder="000000"
            autoComplete="one-time-code"
            autoFocus
          />
          {fieldErrors.code && <p className={styles.errorText}>{fieldErrors.code}</p>}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="reset-new-password" className={styles.label}>New Password</label>
          <input
            id="reset-new-password"
            type="password"
            className={`${styles.input} ${fieldErrors.newPassword ? styles.inputError : ''}`}
            value={newPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
            placeholder="Minimum 8 characters"
            autoComplete="new-password"
          />
          {fieldErrors.newPassword && <p className={styles.errorText}>{fieldErrors.newPassword}</p>}
        </div>

        <button type="submit" className={styles.button} disabled={loading}>
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>

        <div className={styles.footer}>
          <button type="button" className={styles.linkButton} onClick={() => switchMode('login')}>
            Back to sign in
          </button>
        </div>
      </form>
    );
  }

  function renderResetDone() {
    return (
      <div>
        <h1 className={styles.title}>Password Reset! ✓</h1>
        <p className={styles.subtitle}>You can now sign in with your new password.</p>
        <button
          type="button"
          className={styles.button}
          onClick={() => switchMode('login')}
        >
          Sign In
        </button>
      </div>
    );
  }

  /* ---------- Main render ---------- */

  function renderContent() {
    if (mode === 'register') {
      switch (registerStep) {
        case 'credentials':
          return renderRegisterCredentials();
        case 'nickname':
          return renderNicknameStep();
        case 'privacy-consent':
          return renderPrivacyConsent();
        case 'email-verify':
          return renderEmailVerify();
        case 'done':
          return renderRegisterDone();
      }
    }

    if (mode === 'login') {
      switch (loginStep) {
        case 'credentials':
          return renderLoginCredentials();
        case 'done':
          return renderLoginDone();
      }
    }

    if (mode === 'forgot-password') {
      switch (resetStep) {
        case 'enter-email':
          return renderResetEmail();
        case 'enter-code':
          return renderResetCode();
        case 'done':
          return renderResetDone();
      }
    }

    return null;
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.authCard}>
        {renderContent()}
      </div>
    </div>
  );
}

export default AuthPage;
